import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock,
  Database, Wifi, WifiOff, ChevronDown, ChevronRight,
} from "lucide-react";

const API = '/api';

const STATUS_ICON = {
  completed: <CheckCircle size={14} className="text-[#107C10]" />,
  failed: <XCircle size={14} className="text-[#A4262C]" />,
  running: <RefreshCw size={14} className="text-[#0F6CBD] animate-spin" />,
};

export default function SyncStatus() {
  const [status, setStatus] = useState(null);
  const [missing, setMissing] = useState(null);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    const [sRes, mRes, aRes] = await Promise.all([
      axios.get(`${API}/sync/status`),
      axios.get(`${API}/sync/missing-fields`),
      axios.get(`${API}/sync/field-audit?limit=30`),
    ]);
    setStatus(sRes.data);
    setMissing(mRes.data);
    setAudits(aRes.data.audits);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const runSync = async () => {
    setSyncing(true);
    try {
      const resp = await axios.post(`${API}/sync/run`);
      toast.success(`Sync complete: ${resp.data.users_synced} users, ${resp.data.fields_updated} fields updated`);
      fetchAll();
    } catch {
      toast.error("Sync failed");
    }
    setSyncing(false);
  };

  if (loading) return <div className="p-8 text-sm text-[#5E5E5E]">Loading sync status...</div>;

  return (
    <div className="p-8 max-w-[1600px]" data-testid="sync-status-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Directory Sync</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">Microsoft Graph user sync status and field audit</p>
        </div>
        <Button onClick={runSync} disabled={syncing} className="bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm" data-testid="run-sync-btn">
          <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing..." : "Run Sync Now"}
        </Button>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <div className="bg-white border border-[#E1DFDD] rounded-sm p-5">
          <div className="flex items-center gap-2 mb-2">
            {status?.entra_configured ? <Wifi size={16} className="text-[#107C10]" /> : <WifiOff size={16} className="text-[#D83B01]" />}
            <span className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E]">Data Source</span>
          </div>
          <p className="text-lg font-heading font-semibold text-[#171717]">{status?.mode === "mock" ? "Mock Data" : "Microsoft Graph"}</p>
          <p className="text-[10px] text-[#5E5E5E] mt-0.5">{status?.entra_configured ? "Azure credentials configured" : "Using mock — set Azure creds to connect"}</p>
        </div>
        <div className="bg-white border border-[#E1DFDD] rounded-sm p-5">
          <div className="flex items-center gap-2 mb-2">
            <Database size={16} className="text-[#0F6CBD]" />
            <span className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E]">Directory Users</span>
          </div>
          <p className="text-lg font-heading font-semibold text-[#0F6CBD]">{status?.total_users}</p>
        </div>
        <div className="bg-white border border-[#E1DFDD] rounded-sm p-5">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-[#D83B01]" />
            <span className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E]">Missing Fields</span>
          </div>
          <p className="text-lg font-heading font-semibold text-[#D83B01]">{missing?.users_with_gaps || 0}</p>
          <p className="text-[10px] text-[#5E5E5E] mt-0.5">users with data gaps</p>
        </div>
        <div className="bg-white border border-[#E1DFDD] rounded-sm p-5">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className="text-[#5E5E5E]" />
            <span className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E]">Last Sync</span>
          </div>
          <p className="text-sm font-medium text-[#171717]">
            {status?.last_sync ? new Date(status.last_sync.completed_at || status.last_sync.started_at).toLocaleString() : "Never"}
          </p>
          {status?.last_sync && <p className="text-[10px] text-[#5E5E5E] mt-0.5">{status.last_sync.status}</p>}
        </div>
      </div>

      {/* Sync history */}
      {status?.recent_jobs?.length > 0 && (
        <div className="mt-6 bg-white border border-[#E1DFDD] rounded-sm">
          <div className="px-5 py-3 border-b border-[#E1DFDD]">
            <h2 className="font-heading text-base font-medium text-[#171717]">Sync History</h2>
          </div>
          <table className="w-full text-sm text-left" data-testid="sync-history-table">
            <thead>
              <tr className="text-xs uppercase bg-[#FAFAFA] text-[#171717] border-b border-[#E1DFDD]">
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold">Mode</th>
                <th className="px-4 py-2 font-semibold">Started</th>
                <th className="px-4 py-2 font-semibold">Synced</th>
                <th className="px-4 py-2 font-semibold">Created</th>
                <th className="px-4 py-2 font-semibold">Updated</th>
                <th className="px-4 py-2 font-semibold">Fields Changed</th>
              </tr>
            </thead>
            <tbody>
              {status.recent_jobs.map((j) => (
                <tr key={j.id} className="border-b border-[#E1DFDD] text-[#5E5E5E]">
                  <td className="px-4 py-2 flex items-center gap-1.5">{STATUS_ICON[j.status]} {j.status}</td>
                  <td className="px-4 py-2 font-mono text-xs">{j.mode}</td>
                  <td className="px-4 py-2 text-xs">{new Date(j.started_at).toLocaleString()}</td>
                  <td className="px-4 py-2">{j.users_synced}</td>
                  <td className="px-4 py-2">{j.users_created}</td>
                  <td className="px-4 py-2">{j.users_updated}</td>
                  <td className="px-4 py-2">{j.fields_updated}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Missing fields report */}
      {missing?.report?.length > 0 && (
        <div className="mt-6 bg-white border border-[#E1DFDD] rounded-sm">
          <div className="px-5 py-3 border-b border-[#E1DFDD]">
            <h2 className="font-heading text-base font-medium text-[#171717]">Missing Field Report</h2>
          </div>
          <table className="w-full text-sm text-left" data-testid="missing-fields-table">
            <thead>
              <tr className="text-xs uppercase bg-[#FAFAFA] text-[#171717] border-b border-[#E1DFDD]">
                <th className="px-4 py-2 font-semibold">User</th>
                <th className="px-4 py-2 font-semibold">Required Missing</th>
                <th className="px-4 py-2 font-semibold">Recommended Missing</th>
              </tr>
            </thead>
            <tbody>
              {missing.report.map((r) => (
                <tr key={r.email} className="border-b border-[#E1DFDD] text-[#5E5E5E]">
                  <td className="px-4 py-2">
                    <p className="font-medium text-[#171717]">{r.name}</p>
                    <p className="text-xs font-mono">{r.email}</p>
                  </td>
                  <td className="px-4 py-2">
                    {r.required_missing.length > 0 ? r.required_missing.map((f) => (
                      <span key={f} className="inline-block px-1.5 py-0.5 mr-1 mb-0.5 bg-[#A4262C]/10 text-[#A4262C] text-[10px] rounded-sm">{f}</span>
                    )) : <span className="text-[10px] text-[#107C10]">All present</span>}
                  </td>
                  <td className="px-4 py-2">
                    {r.recommended_missing.length > 0 ? r.recommended_missing.map((f) => (
                      <span key={f} className="inline-block px-1.5 py-0.5 mr-1 mb-0.5 bg-[#D83B01]/10 text-[#D83B01] text-[10px] rounded-sm">{f}</span>
                    )) : <span className="text-[10px] text-[#107C10]">All present</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Field audit */}
      <div className="mt-6 bg-white border border-[#E1DFDD] rounded-sm">
        <button onClick={() => setShowAudit(!showAudit)} className="w-full px-5 py-3 border-b border-[#E1DFDD] flex items-center justify-between text-left" data-testid="toggle-audit-btn">
          <h2 className="font-heading text-base font-medium text-[#171717]">Field Mapping Audit</h2>
          {showAudit ? <ChevronDown size={16} className="text-[#5E5E5E]" /> : <ChevronRight size={16} className="text-[#5E5E5E]" />}
        </button>
        {showAudit && (
          <div className="p-5">
            {audits.length === 0 ? (
              <p className="text-sm text-[#5E5E5E]">No field changes recorded yet. Run a sync to see field-level audit trail.</p>
            ) : (
              <div className="space-y-3">
                {audits.map((a, i) => (
                  <div key={i} className="border border-[#E1DFDD] rounded-sm p-3">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="font-mono text-[#0F6CBD]">{a.user_email}</span>
                      <span className="text-[#5E5E5E]">{new Date(a.timestamp).toLocaleString()}</span>
                    </div>
                    {a.changes?.map((c, j) => (
                      <div key={j} className="text-[11px] flex items-center gap-2 py-0.5">
                        <span className="font-mono text-[#5E5E5E] w-32">{c.field}</span>
                        <span className="text-[#A4262C] line-through">{c.old || "null"}</span>
                        <span className="text-[#5E5E5E]">&rarr;</span>
                        <span className="text-[#107C10]">{c.new}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
